import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { notificationSound } from '@/lib/notification-sound'
import { browserNotifications } from '@/lib/browser-notifications'
import { notificationDeepLink } from '@/lib/notification-link'

interface BusEvent {
  id: string
  subject: string | null
  body: string
  eventType: string | null
  category: string | null
  entityType: string | null
  entityId: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export function useNotificationStream() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) return

    // Primárny access token nesmie do URL (skončil by v logoch) — pred každým
    // pripojením sa mintuje krátkodobý single-purpose ticket. EventSource
    // auto-reconnect by recykloval expirovanú URL, preto reconnect riešime
    // sami: nový ticket + backoff.
    let es: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let disposed = false

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        void connect()
      }, 5000)
    }

    const connect = async () => {
      if (disposed) return
      let ticket: string
      try {
        const { data } = await api.post<{ ticket: string }>('/notifications/stream-ticket')
        ticket = data.ticket
      } catch {
        scheduleReconnect()
        return
      }
      if (disposed) return

      es = new EventSource(
        `${API_BASE}/api/v1/notifications/stream?ticket=${encodeURIComponent(ticket)}`,
      )

      es.onmessage = (e) => {
        let event: BusEvent | null = null
        try {
          event = JSON.parse(e.data) as BusEvent
        } catch {
          return
        }
        if (!event) return

        qc.invalidateQueries({ queryKey: ['notifications'] })

        notificationSound.play()

        browserNotifications.show(
          {
            title: event.subject ?? 'Nová notifikácia',
            body: event.body,
            tag: event.id,
            link: notificationDeepLink(event),
          },
          (path) => navigateRef.current(path),
        )

        // Úprava fotky (AI) sa dá zavrieť a čakať — okrem zvončeka aj toast s rýchlym otvorením editora.
        if (event.eventType === 'property.photo_edit_finished') {
          const link = notificationDeepLink(event)
          toast(event.subject ?? 'Úprava fotky je hotová', {
            duration: 10_000,
            ...(link ? { action: { label: 'Otvoriť', onClick: () => navigateRef.current(link) } } : {}),
          })
        }
      }

      es.onerror = () => {
        es?.close()
        es = null
        scheduleReconnect()
      }
    }

    void connect()

    return () => {
      disposed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      es?.close()
    }
  }, [qc])
}
